import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Kbd } from './shared';

const DocCard: React.FC<{ title: string; description: string; to: string }> = ({
  title,
  description,
  to
}) => (
  <Link
    to={to}
    className="block no-underline border border-surface-hover rounded-[12px] p-[18px] bg-surface hover:border-blue hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(34,211,238,0.1)] transition-all"
  >
    <h3 className="font-display text-[15px] font-bold text-content-primary mb-[6px] tracking-[-0.2px]">
      {title}
    </h3>
    <p className="m-0 text-[13px] text-slate-500 leading-[1.6]">{description}</p>
  </Link>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="font-display text-[12px] font-bold uppercase tracking-[0.06em] text-slate-400 mb-[12px] mt-[36px]">
    {children}
  </h2>
);

/** The Ctrl+Alt+1/2/3 chord, as one unit — key names are never translated. */
const LevelKeys: React.FC = () => (
  <>
    <Kbd>Ctrl</Kbd>+<Kbd>Alt</Kbd>+<Kbd>1</Kbd>/<Kbd>2</Kbd>/<Kbd>3</Kbd>
  </>
);

const LINK = 'text-blue-link no-underline font-medium';

const SubdivsDocsLanding: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <h1 className="font-display text-[24px] md:text-[30px] font-extrabold text-content-primary mb-[14px] tracking-[-0.6px] break-words">
        {t('subdivsDocs.landing.title')}
      </h1>
      <p className="text-[15px] leading-[1.7] text-content-secondary">
        {t('subdivsDocs.landing.intro')}
      </p>

      <div className="mt-[16px] rounded-[12px] border border-blue bg-surface p-[16px]">
        <p className="m-0 text-[14px] leading-[1.7] text-content-secondary">
          <strong className="text-content-primary">
            {t('subdivsDocs.landing.quickestLabel')}
          </strong>{' '}
          <Trans
            i18nKey="subdivsDocs.landing.quickestBody"
            components={{ keys: <LevelKeys /> }}
          />
        </p>
      </div>

      <SectionTitle>{t('subdivsDocs.landing.gettingStarted')}</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
        <DocCard
          title={t('subdivsDocs.layout.installation')}
          description={t('subdivsDocs.landing.installationDesc')}
          to="/squarebit-subdivs/docs/installation"
        />
        <DocCard
          title={t('subdivsDocs.layout.licensing')}
          description={t('subdivsDocs.landing.licensingDesc')}
          to="/squarebit-subdivs/docs/licensing"
        />
      </div>

      <SectionTitle>{t('subdivsDocs.landing.components')}</SectionTitle>
      <p className="text-[14px] leading-[1.7] text-content-secondary mb-[14px]">
        {t('subdivsDocs.landing.componentsIntro')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
        <DocCard
          title={t('subdivsDocs.layout.geometryCaches')}
          description={t('subdivsDocs.landing.geometryCachesDesc')}
          to="/squarebit-subdivs/docs/geometry-caches"
        />
        <DocCard
          title={t('subdivsDocs.layout.skeletalMeshes')}
          description={t('subdivsDocs.landing.skeletalMeshesDesc')}
          to="/squarebit-subdivs/docs/skeletal-meshes"
        />
        <DocCard
          title={t('subdivsDocs.layout.staticMeshes')}
          description={t('subdivsDocs.landing.staticMeshesDesc')}
          to="/squarebit-subdivs/docs/static-meshes"
        />
        <DocCard
          title={t('subdivsDocs.layout.grooms')}
          description={t('subdivsDocs.landing.groomsDesc')}
          to="/squarebit-subdivs/docs/grooms"
        />
      </div>

      <div className="border-t border-surface-hover pt-[24px] mt-[40px]">
        <p className="mb-[4px] text-[13px] font-semibold text-content-primary">
          {t('subdivsDocs.landing.needHelp')}
        </p>
        <p className="m-0 text-[13px] text-slate-500 leading-[1.6]">
          <Trans
            i18nKey="subdivsDocs.landing.needHelpText"
            components={{
              // `mp` carries its own text (the nav's own My Products label) so
              // the docs and the header can never disagree.
              mp: (
                <Link to="/management" className={LINK}>
                  {t('header.myProducts')}
                </Link>
              ),
              contact: <Link to="/contact" className={LINK} />
            }}
          />
        </p>
      </div>
    </article>
  );
};

export default SubdivsDocsLanding;
